<template>
  <div>
    <PaginationController
      ref="historyTable"
      v-model="historyScannedItems"
      getDataActionName="getHistoryScannedItems"
      :historyLimit="historyLimit"
      :haveStartDate="filterBy !== 'tracking'"
      :haveNoEndDate="filterBy === 'tracking'"
      :actionPredicates="historyItemPredicates"
    >
      <template v-slot:dataTable>
        <v-data-table
          :headers="headers"
          :items="historyScannedItems"
          item-key="_key"
          hide-actions
        >
          <template v-slot:items="props">
            <td class="text-xs-left">{{ props.item.type }}</td>
            <td class="text-xs-left">{{ carrierMap[props.item.carrier] }}</td>
            <td class="text-xs-left">{{ serviceTypeMap[props.item.serviceType] }}</td>
            <td class="text-xs-left">{{ props.item.trackings && props.item.trackings.length }}</td>
            <td class="text-xs-left" >{{ toDateYYYYMMDDHHmm(props.item.createTime) }}</td>
            <td class="text-xs-left">{{ getSiteNameFromKey(props.item.siteKey) }}</td>
            <td class="text-xs-left">{{ props.item.workerName }}</td>
            <td class="text-xs-center">
              <v-layout justify-center>
                <v-btn color="primary" flat @click.stop="showEditlDialog(props.item)">{{ $t('edit') }}</v-btn>
                <v-btn color="primary" flat @click.stop="showDetailDialog(props.item)">{{ $t('details') }}</v-btn>
              </v-layout>
            </td>
          </template>
        </v-data-table>
      </template>
      <template v-slot:beforeMenu>
        <v-layout align-baseline>
          <v-flex xs3 mr-3>
            <v-autocomplete
              :items="[
                {name: $t('type'), value: 'type'},
                {name: $t('tracking'), value: 'tracking'}
              ]"
              item-value="value"
              item-text="name"
              v-model="filterBy"
              :label="$t('filterBy')"></v-autocomplete>
          </v-flex>
          <v-flex xs3 mr-3 v-if="filterBy === 'type'">
            <v-select
              :items="[
                {name: $t('inbound'), value: 'inbound'},
                {name: $t('outbound'), value: 'outbound'},
              ]"
              item-value="value"
              item-text="name"
              v-model="typeFilter"
              label="Type"/>
          </v-flex>
          <v-flex xs3 mr-3 v-if="filterBy === 'type'">
            <v-autocomplete
              :items="[
                {name: '-- ALL --', value: ''},
                ...Object.entries(carrierMap).map(([key, value]) => {
                  return {
                    name: value,
                    value: key
                  }
                })
              ]"
              item-value="value"
              item-text="name"
              v-model="carrierFilter"
              :label="$t('carrier')"></v-autocomplete>
          </v-flex>
          <v-flex xs5 v-else>
            <v-text-field
              v-model="trackingFilter"
              @keyup.enter="() => $refs['historyTable'].$$initiateTable()"
              :label="$t('searchByTracking')"/>
          </v-flex>
          <v-btn
            color="primary"
            small
            :disabled="historyScannedItems.length === 0"
            @click="exportToExcel"
          >
            {{ $t('export') }}
          </v-btn>
        </v-layout>
      </template>
    </PaginationController>
    <PrescanHistoryEditPopup
      v-model="itemEditDialog"
      v-if="itemEditDialog"
      :item="itemInEdit"
      :carrierMap="carrierMap"
      :serviceTypeMap="serviceTypeMap"
      @onSubmitted="updateItem"/>
    <ChatRoom
      :title="$t('details')"
      v-model="itemDetailDialog"
      v-if="itemDetailDialog"
      enableFileUpload
      createConnection
      v-bind="chatRoomBind"
    >
     <template v-slot:issue>
        <v-card hover raised>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs6>
                <strong>{{ $t('createTime') }}: </strong>&nbsp; {{ toDateYYYYMMDDHHmm(itemInEdit.createTime) }}
              </v-flex>
              <v-flex xs6>
                <strong>{{ $t('quantity') }}: </strong>&nbsp; {{ itemInEdit.trackings && itemInEdit.trackings.length }}
              </v-flex>
              <v-flex xs6>
                <strong>{{ $t('type') }}: </strong>&nbsp; {{ itemInEdit.type }}
              </v-flex>
              <v-flex xs6>
                <strong>{{ $t('carrier') }}: </strong>&nbsp; {{ itemInEdit.carrier }}
              </v-flex>
              <v-flex xs6>
                <strong>{{ $t('workerName') }}: </strong>&nbsp; {{ itemInEdit.workerName }}
              </v-flex>
              <v-flex xs6>
                <strong>{{ $t('serviceType') }}: </strong>&nbsp; {{ serviceTypeMap[itemInEdit.serviceType] }}
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex>
                <strong>{{ $t('note') }}: </strong>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex style="white-space: pre-wrap; overflow: auto; word-wrap: break-word;">{{ itemInEdit.note }}</v-flex>
            </v-layout>
            <v-divider my-1></v-divider>

            <v-layout>
              <v-flex>
                <strong>{{ $t('trackings') }}:</strong>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs4 v-for="tracking in itemInEdit.trackings" :key="tracking">
                <span :class="(itemInEdit.uploadedTrackings || []).includes(tracking) || itemInEdit.type === 'outbound' ? '' : 'non-uploaded-tracking'">{{ tracking }}</span>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <FsFileChip
                v-for="(file, index) in itemInEdit.uploadedFiles"
                :file="file"
                :key="'file' + index"
                width="100%"
                aspect-ratio="1.7"
                class="ma-2"
              />
            </v-layout>
          </v-card-text>
        </v-card>
      </template>
    </ChatRoom>
  </div>
</template>

<script>

import {exportListToXlsx, timeTools} from '@/utils/tools'
import PaginationController from './PaginationController'
import ChatRoom from './ChatRoom'
import SimpleTextPopup from './SimpleTextPopup'
import PrescanHistoryEditPopup from './PrescanHistoryEditPopup'
import FsFileChip from './FsFileChip'

export default {
  name: 'PrescanHistoryTab',
  components: {
    PaginationController,
    SimpleTextPopup,
    PrescanHistoryEditPopup,
    ChatRoom,
    FsFileChip
  },
  mixins: [
    timeTools
  ],
  data () {
    return {
      historyScannedItems: [],
      historyLimit: 25,
      typeFilter: 'inbound',
      carrierFilter: '',
      filterBy: 'type',
      trackingFilter: '',
      itemInEdit: {},
      itemDetailDialog: false,
      itemEditDialog: false,
      chatRoomBind: {}
    }
  },
  computed: {
    historyItemPredicates () {
      if (this.filterBy === 'type') {
        let rtn = [{
          field: 'type',
          compare: '==',
          value: this.typeFilter
        }]
        if (this.carrierFilter) {
          rtn.push({
            field: 'carrier',
            compare: '==',
            value: this.carrierFilter
          })
        }
        return rtn
      }
      if (this.trackingFilter) {
        return [{
          field: 'trackings',
          compare: 'array-contains',
          value: this.trackingFilter.trim().toUpperCase()
        }]
      }
      return []
    },
    packageQtySum () {
      return this.historyScannedItems.reduce((acc, item) => acc + item.trackings.length, 0)
    },
    headers () {
      return [
        { text: this.$t('type'), value: 'type', align: 'left', sortable: false },
        { text: this.$t('carrier'), value: 'carrier', align: 'left', sortable: false },
        { text: this.$t('serviceType'), value: 'serviceType', align: 'left', sortable: false },
        { text: `${this.$t('packageQty')} (${this.packageQtySum})`, value: '', align: 'left', sortable: false },
        { text: this.$t('createTime'), value: 'createTime', align: 'left', sortable: false },
        { text: this.$t('site'), value: 'siteKey', align: 'left', sortable: false },
        { text: this.$t('operator'), value: 'workerName', align: 'left', sortable: false },
        { text: this.$t('action'), value: '', align: 'center', sortable: false, width: '10%' }
      ]
    },
    warehousesSites () {
      return this.$store.getters.warehousesSites
    },
    defaultSite () {
      return window.localStorage.getItem('defaultSite' + this.$store.getters.uid)
    }
  },
  methods: {
    exportToExcel () {
      const headers = ['Type', 'Carrier', 'Service Type', 'Package Qty', 'Create Time', 'Site', 'Operator', 'Uploaded Trackings', 'All Trackings']
      const dataToExport = this.historyScannedItems.reduce((arr, item) => {
        const { trackings = [] } = item
        trackings.forEach((tracking, index) => {
          arr.push([
            item.type,
            this.carrierMap[item.carrier],
            this.serviceTypeMap[item.serviceType],
            index === 0 ? item.trackings?.length || 0 : '',
            this.toDateYYYYMMDDHHmm(item.createTime),
            this.getSiteNameFromKey(item.siteKey),
            item.workerName,
            (item.uploadedTrackings || []).join(', '),
            tracking
          ])
        })
        return arr
      }, [])
      return exportListToXlsx([headers, ...dataToExport], `prescan-${new Date().toLocaleDateString()}`)
    },
    uploadScannedTrackings () {
      if (!this.trackings) return this.$store.dispatch('showToast', {info: this.$t('emptyTracking'), level: 'error'})
      return this.$store.dispatch('uploadScannedTrackings', {
        trackings: this.trackings,
        type: this.isInbound ? 'inbound' : 'outbound',
        carrier: this.carrier,
        uploadedFiles: this.uploadedFiles,
        serviceType: this.serviceType
      })
        .then(() => {
          this.$store.dispatch('showToast', {info: this.$t('uploadSuccess'), level: 'success'})
          this.trackings = ''
          this.$refs.fileUploader.$$cleanData()
          this.carrier = 'usps'
          this.serviceType = 'all'
        })
    },
    downloadFiles (item) {
      item.zipFile && this.$store.dispatch('downloadFile', {
        fullPath: item.zipFile,
        name: 'file.zip'
      })
    },
    showDetailDialog (item) {
      this.itemInEdit = item
      this.itemDetailDialog = true
      this.chatRoomBind = {
        docPath: ['warehouses', this.$store.getters.activeWarehouse, 'scannedTrackings', item._key],
        comments: item.comments || []
      }
    },
    showEditlDialog (item) {
      this.itemInEdit = item
      this.itemEditDialog = true
    },
    updateItem (e) {
      const target = this.historyScannedItems.find(item => item === this.itemInEdit)
      const {isInbound} = e
      Object.assign(target, {...e, type: isInbound ? 'inbound' : 'outbound'})
    },
    getSiteNameFromKey (siteKey) {
      return (this.warehousesSites.find(ele => ele.key === siteKey) || {}).siteName
    }
  },
  props: {
    carrierMap: Object,
    serviceTypeMap: Object
  }
}
</script>

<style>
.non-uploaded-tracking {
  border: 1px solid;
  padding: 0 3px;
  border-color: deepskyblue;
}
</style>



// WEBPACK FOOTER //
// src/components/PrescanHistoryTab.vue